import { useEffect } from 'react';
import lozad from 'lozad';

interface UseLazyBackgroundImageProps {
    uniqueId: string;
}

/**
 * Hook to initialize lozad for a specific element with unique ID and 'lozad' class
 * Components should add 'lozad' class and data-desktop-img/data-mobile-img attributes
 */
export function useLazyBackgroundImage({ uniqueId }: UseLazyBackgroundImageProps) {
    useEffect(() => {
        if (!uniqueId) {
            return;
        }

        // Target specific element by unique ID that also has lozad class
        const selector = `#${uniqueId}.lozad`;

        // Initialize lozad with custom load function for responsive images
        const observer = lozad(selector, {
            rootMargin: '0px 0px', // Only load when element actually enters viewport
            threshold: 0.1, // Load when 10% of element is visible
            load: function (el) {
                // Determine which image to use based on viewport width
                const isDesktop = window.innerWidth >= 750;
                const desktopImg = el.getAttribute('data-desktop-img');
                const mobileImg = el.getAttribute('data-mobile-img');

                const imageToUse = isDesktop ? desktopImg || mobileImg : mobileImg || desktopImg;

                if (imageToUse) {
                    // Set background properties and image
                    el.style.backgroundImage = `url("${imageToUse}")`;

                    // Set up resize listener for responsive switching
                    const handleResize = () => {
                        const isDesktopNow = window.innerWidth >= 750;
                        const newImageToUse = isDesktopNow ? desktopImg || mobileImg : mobileImg || desktopImg;

                        if (newImageToUse && el.style.backgroundImage !== `url("${newImageToUse}")`) {
                            // Start fade out using CSS classes
                            el.classList.remove('lozad-loaded');
                            el.classList.add('lozad-loading');

                            // Switch image after short delay
                            setTimeout(() => {
                                el.style.backgroundImage = `url("${newImageToUse}")`;

                                // Fade back in
                                setTimeout(() => {
                                    el.classList.remove('lozad-loading');
                                    el.classList.add('lozad-loaded');
                                }, 50);
                            }, 500); // Wait for fade-out to complete
                        }
                    };

                    window.addEventListener('resize', handleResize);

                    // Store cleanup function
                    (el as any)._lazyBgCleanup = () => {
                        window.removeEventListener('resize', handleResize);
                    };
                }

                el.classList.add('lazy-bg-loaded');
            },
            loaded: function (el) {
                // This fires after load completes and image is ready - perfect for fade-in!
                // Remove loading class and add loaded class for fade-in effect
                el.classList.remove('lozad-loading');
                el.classList.add('lozad-loaded');
            },
        }); // Wait for DOM and images to finish loading before starting observation
        const startObservation = () => {
            observer.observe();
        };

        // Check if DOM is already fully loaded
        if (document.readyState === 'complete') {
            // DOM and all resources are loaded, but add small delay for layout settling
            setTimeout(startObservation, 100);
        } else {
            // Wait for window load event (DOM + all images/resources)
            const handleLoad = () => {
                // Add small delay after load to ensure layout is stable
                setTimeout(startObservation, 100);
                window.removeEventListener('load', handleLoad);
            };
            window.addEventListener('load', handleLoad);
        }

        return () => {
            // Cleanup resize listener for this specific element
            const element = document.querySelector(selector);
            if (element) {
                const cleanup = (element as any)._lazyBgCleanup;
                if (cleanup) {
                    cleanup();
                }
            }
        };
    }, [uniqueId]);
}
